import React from "react";
import Link from "next/link";
import { ArticleShort } from "lib/types/article";
import { CalloutDato } from "lib/types/homepage";
import { buildArticleBreadcrumbs } from "lib/utils/articleBreadcrumbs";
import Callout from "components/callout/Callout";
import ArticleHeadline from "components/article/ArticleHeadline";

interface FeaturedHeroArticlesProps {
  articles: ArticleShort[];
  callout?: CalloutDato;
}

const maxArticlesToShow = 3;

const buildContentList = (
  articles: ArticleShort[],
  callout?: CalloutDato
): (ArticleShort | { _type: "callout"; callout: CalloutDato })[] => {
  const contentToDisplay: (
    | ArticleShort
    | { _type: "callout"; callout: CalloutDato }
  )[] = [];

  articles.slice(0, maxArticlesToShow).forEach((article) => {
    contentToDisplay.push(article);
  });

  if (callout && contentToDisplay.length < maxArticlesToShow) {
    contentToDisplay.push({ _type: "callout", callout });
  } else if (callout && contentToDisplay.length === maxArticlesToShow) {
    contentToDisplay[contentToDisplay.length - 1] = {
      _type: "callout",
      callout,
    };
  }

  return contentToDisplay;
};

const FeaturedHeroArticles = ({
  articles,
  callout,
}: FeaturedHeroArticlesProps) => {
  const contentToDisplay = buildContentList(articles, callout);

  return (
    <div className="flex w-full flex-col gap-2">
      {contentToDisplay.map((item, index) => {
        if ("_type" in item && item._type === "callout") {
          return (
            <Callout
              key={`callout-${index}`}
              callout={item.callout}
              href={item.callout.buttonCta || undefined}
            />
          );
        }

        const article = item as ArticleShort;
        const breadcrumbItems = buildArticleBreadcrumbs(article, {
          sectorIsLink: true,
          categoryIsLink: false,
        });

        return (
          <article key={article.id || article.url} className="group">
            <div
              className={`flex flex-col gap-2 border-l-[1px] ${article.isProContent ? "border-pro" : "border-sifted"} h-[152px] p-4 transition-colors`}
            >
              {breadcrumbItems.length > 0 && (
                <div className="flex flex-wrap items-center gap-1">
                  {breadcrumbItems}
                </div>
              )}

              <Link
                href={article.url}
                aria-label={article.title}
                className="flex flex-col gap-2"
              >
                <ArticleHeadline
                  title={article.title}
                  isProContent={article.isProContent}
                />

                {article.excerpt && (
                  <p className="line-clamp-2 text-regular-16 text-brand-neutral">
                    {article.excerpt}
                  </p>
                )}
              </Link>
            </div>
          </article>
        );
      })}
    </div>
  );
};

export default FeaturedHeroArticles;
